import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

/**
 * MultiAssigneePicker — lightweight multi-select dropdown.
 *
 * Props:
 *   employees        : [{ employee_id, name, department }]
 *   value            : string[] — selected employee_ids
 *   onChange         : (ids: string[]) => void
 *   placeholder      : string
 *   testid           : dom data-testid prefix
 */
export default function MultiAssigneePicker({
  employees = [],
  value = [],
  onChange,
  placeholder = 'Select assignees',
  testid = 'multi-assignee',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(v => v !== id));
    else onChange([...value, id]);
  };

  const remove = (id, ev) => { ev?.stopPropagation(); onChange(value.filter(v => v !== id)); };

  const filtered = employees.filter(e =>
    !q || e.name?.toLowerCase().includes(q.toLowerCase()) || e.department?.toLowerCase().includes(q.toLowerCase())
  );

  const selectedEmps = value.map(v => employees.find(e => e.employee_id === v)).filter(Boolean);

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full min-h-[40px] bg-black/40 border border-white/10 hover:border-white/20 text-white rounded-md px-3 py-1.5 text-sm flex items-start gap-2 flex-wrap cursor-pointer"
        data-testid={`${testid}-trigger`}
      >
        <div className="flex-1 flex flex-wrap gap-1 items-center min-w-0">
          {selectedEmps.length === 0 ? (
            <span className="text-slate-500">{placeholder}</span>
          ) : selectedEmps.map(e => (
            <span
              key={e.employee_id}
              className="inline-flex items-center gap-1 bg-indigo-500/15 text-indigo-200 border border-indigo-500/30 rounded px-2 py-0.5 text-[11px]"
              data-testid={`${testid}-chip-${e.employee_id}`}
            >
              {e.name}
              <X
                size={10}
                className="hover:text-rose-300 cursor-pointer"
                onClick={(ev) => remove(e.employee_id, ev)}
              />
            </span>
          ))}
        </div>
        <ChevronsUpDown size={14} className="text-slate-500 flex-shrink-0 mt-1" />
      </button>

      {open && (
        <div className="absolute z-[220] left-0 right-0 mt-1 bg-[#0f0f15] border border-white/10 rounded-md shadow-2xl max-h-[260px] flex flex-col overflow-hidden">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="bg-black/40 border-b border-white/10 text-white px-3 py-2 text-xs focus:outline-none"
            data-testid={`${testid}-search`}
          />
          <div className="overflow-y-auto max-h-[220px]">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-slate-500 text-xs">No employees</div>
            ) : filtered.map(e => {
              const checked = value.includes(e.employee_id);
              return (
                <button
                  key={e.employee_id}
                  type="button"
                  onClick={() => toggle(e.employee_id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-white/5 ${checked ? 'bg-indigo-500/10' : ''}`}
                  data-testid={`${testid}-option-${e.employee_id}`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-indigo-600 border-indigo-500' : 'border-white/20'}`}>
                    {checked && <Check size={10} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{e.name}</div>
                    {e.department && <div className="text-[10px] text-slate-500 truncate">{e.department}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
